import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageLayout from "../../../components/AdminPageLayout/AdminPageLayout";
import { Headings } from "../../../components/Headings";
import LoadingMask from "../../../components/LoadingMask/LoadingMask";
import SubmissionReviewCard from "../../../components/SubmissionReviewCard/SubmissionReviewCard";
import { useAppAuthState } from "../../../hooks/useAppAuthState";
import { getPlaceDataSubmissions } from "../../../services/placeService";
import { ResolvedPlaceDataSubmission } from "../../../types/Place";
import { debugLogError } from "../../../utils/debug-log-error";
import "./AdminDataSubmissionPage.scss";

const AdminDataSubmissionPage: React.FC = () => {
  const { isUserAdmin, isAuthLoading } = useAppAuthState();
  const [onlyShowToReview, setOnlyShowToReview] = useState(true);
  const [submissions, setSubmissions] = useState<ResolvedPlaceDataSubmission[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedSubmissions =
        await getPlaceDataSubmissions(onlyShowToReview);
      setSubmissions(fetchedSubmissions);
      setError(null);
    } catch (err: any) {
      debugLogError("fetchSubmissions", err);
      setError(err.message || "Failed to fetch submissions.");
    } finally {
      setIsLoading(false);
    }
  }, [onlyShowToReview]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (isUserAdmin) {
      fetchSubmissions();
    } else {
      setError("Access Denied. You must be an admin to view this page.");
      setIsLoading(false);
    }
  }, [isUserAdmin, isAuthLoading, fetchSubmissions]);

  const pageContent = useMemo(() => {
    if (isLoading || isAuthLoading) {
      return null;
    }

    if (!isUserAdmin) {
      return (
        <div className="alert alert-danger">
          Access Denied. You must be an admin to view this page.
        </div>
      );
    }

    if (error && !submissions.length) {
      return <div className="alert alert-danger">{error}</div>;
    }

    const toggleQueryContentButton = (
      <button
        className="btn btn-outline-primary"
        onClick={() => setOnlyShowToReview(!onlyShowToReview)}
      >
        {onlyShowToReview ? "Show All" : "Show Only To Review"}
      </button>
    );

    if (submissions.length === 0) {
      return (
        <>
          <div className="gapped-buttons">
            <Link to="/admin" className="btn btn-outline-primary">
              &larr; Admin
            </Link>
            {toggleQueryContentButton}
          </div>
          <Headings.Page>Place Data Submissions</Headings.Page>
          {error && <div className="alert alert-warning">{error}</div>}
          <div className="alert alert-info">No submissions found.</div>
        </>
      );
    }

    return (
      <div className="admin-data-submission-page-container">
        <div className="gapped-buttons">
          <Link to="/admin" className="btn btn-outline-primary">
            &larr; Admin
          </Link>
          {toggleQueryContentButton}
        </div>
        <Headings.Page>Place Data Submissions</Headings.Page>
        {error && <div className="alert alert-warning">{error}</div>}
        {submissions.map((submission) => (
          <SubmissionReviewCard
            key={submission.id}
            submission={submission}
            isAdmin={true}
            context="global"
          />
        ))}
      </div>
    );
  }, [
    isLoading,
    isAuthLoading,
    isUserAdmin,
    error,
    submissions,
    onlyShowToReview,
  ]);

  if (isLoading || isAuthLoading) {
    return <LoadingMask message="Loading submissions..." />;
  }

  return (
    <AdminPageLayout title="Data Submissions">{pageContent}</AdminPageLayout>
  );
};

export default AdminDataSubmissionPage;
